import api from "@/lib/api";

/**
 * A photo for Dany's thread, uploaded the way every job photo is (T19 F4).
 *
 * The same endpoint and the same `purpose` as `JobPhotoGrid`'s uploader
 * (`POST /uploads/image`, `job_photo`), because the photo is headed for the
 * same place: the job draft this conversation builds. Only the `file_id`
 * travels into the turn — the server resolves it against the caller's own
 * uploads and never trusts a URL the browser names.
 *
 * Throws `{ code }` with the `uploader.*` key that explains the refusal, so the
 * thread can say it in the reader's language: `pickImage` for something that
 * is not an image, `maxSize` for the panel's limit, `uploadFailed` otherwise.
 */
export async function uploadDanyPhoto(file, { maxMb }) {
  if (!file?.type?.startsWith("image/")) throw { code: "pickImage" };
  if (maxMb && file.size > maxMb * 1024 * 1024) throw { code: "maxSize" };
  const form = new FormData();
  form.append("file", file);
  form.append("purpose", "job_photo");
  try {
    const { data } = await api.post("/uploads/image", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { file_id: data.file_id, url: data.url };
  } catch {
    throw { code: "uploadFailed" };
  }
}

/**
 * Attach a photo to a call in progress (T19 F5) — the voice door onto the same
 * server piece the text turn uses (`services/dany/photos.attach`). Answers with
 * the attachment, what FixyDany read in it, and the lines the browser hands to
 * the live agent.
 *
 * `grant` is this call's, from `/voice/session`: the server files the photo
 * against the call with it, and the agent's `get_attachments` reads that
 * record — without it the photo still lands, but the agent cannot find it.
 */
export async function attachPhotoToCall(threadId, fileId, { anonymousKey, grant } = {}) {
  const headers = anonymousKey ? { "X-Dany-Anonymous-Key": anonymousKey } : {};
  const { data } = await api.post(
    `/voice/threads/${threadId}/photo`, { file_id: fileId, grant: grant || null }, { headers },
  );
  return data;
}
